import type React from 'react';

import * as styles from './styles';
import ChecklistItemDropdownMenu from './ChecklistItemDropdownMenu';
import ChecklistItemIconButton from './ChecklistItemIconButton';
import ChecklistItemOptions, { emptyOptionContent } from './ChecklistItemOptions';
import type { ChecklistFormProps } from './formProps';
import { ErrorMessage, FastField, useFormikContext } from 'formik';

import { ChecklistFieldType } from '@cobliteam/api-lib';

import { CaptionDefault, CaptionError, Checkbox, DefaultInput, type DropdownOption, TrashIcon } from '@cobliteam/base-components';

export const checklistItemDescriptionText = {
  [ChecklistFieldType.CHECK]: 'Item pode ser marcado como feito',
  [ChecklistFieldType.TEXT]: 'Item pode ser respondido com um texto',
  [ChecklistFieldType.MULTI_SELECT]: 'Mais de uma opção pode ser selecionada',
  [ChecklistFieldType.SINGLE_SELECT]: 'Apenas uma opção pode ser selecionada',
};

const getChecklistMetadata = (index: number) => ({
  titleMd: `checklistItems[${index}].title`,
  requiredMd: `checklistItems[${index}].required`,
  typeMd: `checklistItems[${index}].type`,
  contentMd: `checklistItems[${index}].content`,
});

export const checklistDefaultContent = {
  [ChecklistFieldType.CHECK]: { checked: null },
  [ChecklistFieldType.TEXT]: { value: null },
  [ChecklistFieldType.MULTI_SELECT]: { options: [emptyOptionContent] },
  [ChecklistFieldType.SINGLE_SELECT]: { options: [emptyOptionContent] },
};

export interface ChecklistItemProps {
  index: number;
  handleDeleteClick: () => void;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({ index, handleDeleteClick }) => {
  const { values, handleChange, getFieldMeta, setFieldValue } = useFormikContext<ChecklistFormProps>();
  const { required, type } = values.checklistItems[index];
  const { titleMd, requiredMd, typeMd, contentMd } = getChecklistMetadata(index);

  const handleChecklistFieldTypeChange = (option: DropdownOption<ChecklistFieldType>) => {
    setFieldValue(typeMd, ChecklistFieldType[option.key], false);
    setFieldValue(contentMd, checklistDefaultContent[option.key], false);
  };

  return (
    <div className={styles.checklistItem}>
      <div>
        <ChecklistItemDropdownMenu index={index} onClick={handleChecklistFieldTypeChange} />
      </div>
      <div className={styles.checklistItemContent}>
        <div className={styles.checklistItemHeader}>
          <CaptionDefault>{checklistItemDescriptionText[type]}</CaptionDefault>
          <div>
            <FastField onChange={handleChange} name={requiredMd} label='Campo obrigatório' checked={required} as={Checkbox} />
            <ChecklistItemIconButton disableRipple onClick={handleDeleteClick}>
              <TrashIcon />
            </ChecklistItemIconButton>
          </div>
        </div>
        <div>
          <FastField
            onChange={handleChange}
            name={titleMd}
            error={getFieldMeta(titleMd).touched && Boolean(getFieldMeta(titleMd).error)}
            placeholder='Digite a pergunta'
            fullWidth
            as={DefaultInput}
          />
          <div className={styles.checklistItemError}>
            <ErrorMessage name={titleMd}>{(msg) => <CaptionError>{msg}</CaptionError>}</ErrorMessage>
          </div>
        </div>
        <ChecklistItemOptions index={index} contentMd={contentMd} />
      </div>
    </div>
  );
};

export default ChecklistItem;
